/**
 * Lightweight notification event emitter.
 *
 * Allows non-React code (e.g. HTTP interceptors) to trigger UI notifications
 * that the React NotificationBanner component subscribes to.
 */

export type NotificationType = 'error' | 'warning' | 'info'

export interface NotificationAction {
  label: string
  href: string
  /** Open `href` in a new tab (e.g. an external checkout URL). */
  external?: boolean
}

type Listener = (message: string, type: NotificationType, action?: NotificationAction) => void

const listeners = new Set<Listener>()

/** Last emitted messages with timestamps for deduplication. */
const recent = new Map<string, number>()
const DEDUP_WINDOW_MS = 10_000

export function onNotification(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function emitNotification(
  message: string,
  type: NotificationType,
  action?: NotificationAction,
): void {
  // Deduplicate identical messages within a 10-second window
  const key = `${type}:${message}`
  const now = Date.now()
  const lastTime = recent.get(key)
  if (lastTime && now - lastTime < DEDUP_WINDOW_MS) return
  recent.set(key, now)

  // Clean old entries
  for (const [k, t] of recent) {
    if (now - t > DEDUP_WINDOW_MS) recent.delete(k)
  }

  for (const fn of listeners) fn(message, type, action)
}
